/**
 * Web Push Notifications helper utilities.
 */

import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/** Check if browser supports push notifications. */
export const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

/** Current permission state: 'granted' | 'denied' | 'default'. */
export const getPermission = () => {
  if (!('Notification' in window)) return 'denied';
  return Notification.permission;
};

/** Convert base64 VAPID key to Uint8Array (required by PushManager). */
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
};

/** Get existing subscription for current browser (or null). */
export const getCurrentSubscription = async () => {
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.ready;
  return await reg.pushManager.getSubscription();
};

/**
 * Subscribe current device to push notifications.
 * Prompts user for permission if not yet granted.
 * Returns true on success, throws on error.
 */
export const subscribeToPush = async (user, location) => {
  if (!isPushSupported()) {
    throw new Error('Twoja przeglądarka nie wspiera powiadomień push');
  }

  // Request permission
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Odmówiono zgody na powiadomienia');
  }

  // Fetch VAPID public key
  const { data: keyData } = await axios.get(`${API}/api/push/vapid-public-key`);
  const publicKey = keyData.publicKey;

  // Get SW registration
  const reg = await navigator.serviceWorker.ready;

  // Check existing sub
  let subscription = await reg.pushManager.getSubscription();
  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  // Send to backend
  const isDeveloper = user?.username === 'Developer' || user?.role === 'developer';
  await axios.post(`${API}/api/push/subscribe`, {
    userId: user.id,
    username: user.username,
    location: location,
    isDeveloper: isDeveloper,
    subscription: subscription.toJSON(),
  });

  return true;
};

/** Unsubscribe current device from push. */
export const unsubscribeFromPush = async (user) => {
  if (!isPushSupported()) return false;
  const reg = await navigator.serviceWorker.ready;
  const subscription = await reg.pushManager.getSubscription();
  if (!subscription) return true;

  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  try {
    await axios.post(`${API}/api/push/unsubscribe`, {
      userId: user.id,
      endpoint: endpoint,
    });
  } catch (e) {
    // backend usuwanie nieudane, ale lokalnie już odsubskrybowano
    if (process.env.NODE_ENV !== 'production') console.warn('[push] backend unsubscribe failed:', e?.message);
  }
  return true;
};

/** Check backend status for user. */
export const checkPushStatus = async (userId) => {
  try {
    const { data } = await axios.get(`${API}/api/push/status/${userId}`);
    return data;
  } catch {
    return { subscribed: false, deviceCount: 0 };
  }
};

/** Toggle all subscriptions for user (keeps device registered, just enables/disables delivery). */
export const togglePush = async (userId, enabled) => {
  await axios.put(`${API}/api/push/toggle`, { userId, enabled });
};

/** Manual send - manager only. */
export const sendManualPush = async (location, title, body, fromUser) => {
  const { data } = await axios.post(`${API}/api/push/send-manual`, {
    location,
    title,
    body,
    fromUser,
  });
  return data;
};
